import {
  DefaultService as TSAgentDefaultService,
  OpenAPI as TSAgentOpenAPI,
  BalanceService as TSBalanceService,
  SearchService as TSSearchService
} from '@LoopKitchen/agent-client'
import {
  LoopModule,
  AccountingService as TSAccountingService,
  AdminService as TSAdminService,
  BnamesService as TSBnamesService,
  ChainsService as TSChainsService,
  CompassService as TSCompassService,
  DefaultService as TSDefaultService,
  DigitalStoresService as TSDigitalStoresService,
  FpnaService as TSFpnaService,
  JeCustomizationService as TSJeCustomizationService,
  JeFiltersService as TSJeFiltersService,
  LeaderboardService as TSLeaderboardService,
  MarketingService as TSMarketingService,
  ModuleService as TSModuleService,
  NotificationsService as TSNotificationsService,
  OnboardingService as TSOnboardingService,
  OpenAPI as TSOpenAPI,
  OwnersService as TSOwnersService,
  ReportsService as TSReportsService,
  StoreAvailabilityService as TSStoreAvailabilityService,
  UsersService as TSUsersService
} from '@LoopKitchen/frontend-client'
import { DefaultService as TSTruROIAgentDefaultService, OpenAPI as TSTruROIAgentOpenAPI, AdminConversationsService as TSTruROIAgentAdminConversationsService } from '@LoopKitchen/truroi-agent-client'
import { FiltersService as TSAppClientFiltersService, OpenAPI as TSAppClientOpenAPI } from '@LoopKitchen/app-client'
// External dependencies
import { Interceptors } from '@LoopKitchen/frontend-client/lib/core/OpenAPI'
import { captureException } from '@sentry/react'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import get from 'lodash/get'
import posthog from 'posthog-js'

// Extend AxiosRequestConfig to include our custom properties
declare module 'axios' {
  export interface AxiosRequestConfig {
    requestTime?: number
    module?: string
  }
}

// Internal dependencies
import { getTokenCreationFromStorage, getTokenExpireFromStorage } from 'src/services/firebase-compat'
import { userClaimsService } from 'src/services/userClaimsService'
import { sendSlackNotification } from 'src/utils/api'
import { blockedApiEndpointPatterns } from 'src/utils/config/config'
import { getAccessLevel } from 'src/utils/functions/accessLevel'
import { getSession } from 'src/utils/functions/localStorage'
import { addTableIdToRequest, updateTableId } from 'src/utils/functions/openApiSupport'
import { shouldBlockForUser } from 'src/utils/functions/sandbox'
import { getfeLogsOncall } from 'src/utils/functions/slackSupport'
import { lokiLogger } from '../lokiLogger'
import { metricsService } from '../metrices'

// Local utilities
import { getModuleFromPathname } from 'src/utils/functions/moduleUtils'
import { CURRENT_CONFIG } from './utils/api.config'
import { ABORT_CODE, ABORT_ERROR_NAME, ABORT_KEY, ABORT_SKIP_KEY, abortMap } from './utils/constants'
import { DEFAULT_RETRY_CONFIG, RetryConfig, executeWithRetry } from './utils/retry'

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if URL matches a pattern (handles path params)
 */
function urlMatchesPattern(url: string, pattern: string): boolean {
  // Remove query params from URL
  const cleanUrl = url.split('?')[0]

  // Convert pattern with {param} to regex
  const regexPattern = pattern
    .replace(/\{[^}]+\}/g, '[^/]+') // Replace {param} with regex
    .replace(/\//g, '\\/') // Escape slashes

  const regex = new RegExp(`^${regexPattern}$`)
  return regex.test(cleanUrl)
}

/**
 * Check if the request should be blocked for sandbox users
 */
function shouldBlockRequestForSandbox(method: string, url: string): boolean {
  const methodUpper = method.toUpperCase()

  // Check each blocked endpoint pattern
  return blockedApiEndpointPatterns.some((pattern) => {
    const [blockedMethod, blockedUrl] = pattern.split(':')
    return blockedMethod === methodUpper && urlMatchesPattern(url, blockedUrl)
  })
}

/**
 * Tracks HTTP latency metrics for requests
 */
function trackHttpLatencyMetrics(config: any, status: number, type: 'success' | 'error'): void {
  const requestTime = config.requestTime
  const duration = requestTime ? Date.now() - requestTime : 0

  try {
    metricsService.trackHttpLatency(
      {
        method: config.method ?? 'unknown',
        apiPath: config.url ?? 'unknown',
        statusCode: status,
        routePath: window.location.pathname,
        module: config.module || 'unknown'
      },
      duration
    )

    // PostHog latency tracking for backend calls only
    if (
      config.url &&
      (!config.url.startsWith('http') || // Relative URLs
        config.url.includes('tryloop.ai') || // Your APIs
        config.url.includes('run.app')) // Your Google Cloud APIs
    ) {
      const sessionData = getSession('data')
      posthog.capture('API_latency', {
        endpoint: config.url
          .split('?')[0]
          .replace(/\/[0-9a-f-]{36}/g, '/{id}')
          .replace(/\/\d+/g, '/{id}'),
        method: config.method?.toUpperCase() || 'GET',
        latency_ms: duration,
        status_code: status,
        page_path: window.location.pathname,
        organization: sessionData?.org || 'unknown',
        module: getModuleFromPathname(window.location.pathname) || LoopModule.BASE,
        timestamp: Date.now()
      })
    }
  } catch (metricsError) {
    console.warn(`Failed to track HTTP latency for ${type}:`, metricsError)
  }
}

/**
 * Tracks API error metrics and logs to Loki
 */
function trackApiErrorMetrics(error: any): void {
  // Extract info from error, with fallbacks for missing config
  const method = error.config?.method || error.method || 'unknown'
  const url = error.config?.url || error.url || 'unknown'
  const requestTime = error.config?.requestTime
  const duration = requestTime ? Date.now() - requestTime : 0
  const statusCode = getNumericStatusCode(error)
  const errorMessage = getErrorMessage(error)

  // Always log to Loki, even if config is missing
  try {
    lokiLogger.logApiError(url, method, statusCode, errorMessage, error.response?.data || error.data)
  } catch (lokiError) {
    console.warn('Failed to log API error to Loki:', lokiError)
  }

  // Track metrics if we have enough info
  if (error.config) {
    try {
      metricsService.trackApiError(
        method,
        url,
        statusCode,
        error.response?.data?.error?.type || 'unknown',
        error.config.module || 'unknown',
        duration,
        window.location.pathname
      )
    } catch (metricsError) {
      console.warn('Failed to track API error:', metricsError)
    }
  }
}

/**
 * Handles request abort logic and timestamp addition
 */
function handleRequestAbortAndTimestamp(e: AxiosRequestConfig, targetAbortkey: string): void {
  try {
    const headers = e.headers ?? {}
    const hasSkipAbortKey = headers[ABORT_SKIP_KEY] === 'skip'

    if (!CURRENT_CONFIG.enableAbort) return

    if (!hasSkipAbortKey) {
      const currentAbortKey = headers[ABORT_KEY]

      if (currentAbortKey === targetAbortkey) {
        const obj = abortMap.get(currentAbortKey) ?? {}
        const oldController = obj?.controller
        const controller = new AbortController()
        abortMap.set(currentAbortKey, { ...obj, controller })
        e.signal = controller.signal

        if (oldController?.signal.aborted === false) {
          oldController.abort()
        }
      }
    }
  } catch (err) {
    console.log(err)
  }
}

/**
 * Handles response error tracking and notifications
 */
function handleResponseError(error: any): void {
  // Don't return early - we want to log all errors

  // Track metrics if config exists
  if (error.config) {
    trackHttpLatencyMetrics(error.config, error.response?.status || error.status || 0, 'error')
  }

  // Always try to track API errors (it has its own config check)
  trackApiErrorMetrics(error)

  // Get user data for error reporting
  let userObject: Record<string, any> = {}
  try {
    userObject = getSession('data') || {}
  } catch (e) {
    console.error('Error parsing user data from localStorage:', e)
  }

  // Extract error details
  const xCache = get(error, 'response.headers.x-cache', 'No x-cache header found')
  const status_code = getStatusCode(error)
  const browser = navigator && navigator.userAgent ? navigator.userAgent : `No browser found`
  const requestTime = error.config.requestTime
  const errorTime = requestTime ? Date.now() - requestTime : 0
  const message = getErrorMessage(error)
  const apiPath = new URL(String(get(error, 'url', undefined) || get(error, 'config.url', 'unknown')), window.location.origin)?.pathname || 'unknown'

  // Track in PostHog
  posthog.capture('api_error', {
    error,
    method: error.config?.method || 'unknown',
    url: apiPath,
    browser,
    xCache,
    errorTime
  })

  // Check for invalid token
  const hasInvalidToken = message && typeof message === 'string' && message.toLowerCase().includes('invalid authentication')

  const apiUrl = String(get(error, 'url') || get(error, 'config.url') || 'unknown')
  const isLocalhost = apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1') || apiUrl.includes('::1')
  const isProduction = process.env.NODE_ENV === 'production'

  if (isProduction && !isLocalhost) {
    // Send Slack notification
    const slackTitle = [
      `Error message: \`${message}\``,
      hasInvalidToken && `Expired User Token Details:`,
      hasInvalidToken && `Expiration time: \`${getTokenExpireFromStorage()}\``,
      hasInvalidToken && `Creation time: \`${getTokenCreationFromStorage()}\``,
      hasInvalidToken && `Current time: \`${new Date().toUTCString()}\``,
      `Error Code: \`${status_code}\``,
      `User: \`<https://us.posthog.com/project/26198/person/${encodeURIComponent(userObject.email)}|${userObject.email}>\``,
      `Page URL: \`<${window.location.href}|${window.location.pathname}>\``,
      `API URL: \`${apiUrl}\``,
      `Org: \`${userObject?.org}\``,
      `Access Level: \`${getAccessLevel(userObject)}\``,
      `Error log: \`\`\`${JSON.stringify(error, null, 2)}\`\`\``
    ]
      .filter(Boolean)
      .join('\n')

    sendSlackNotification({
      title: slackTitle,
      message: `Error occurred while calling API: ${error.config?.method?.toUpperCase() || 'unknown'} ${apiPath}`,
      channel: getfeLogsOncall()
    })
  }

  // Capture in Sentry with user-friendly message
  error.message = message // Use the already extracted message from line 236
  captureException(error)
}

// ============================================================================
// Exported Functions
// ============================================================================

export function createAbortKey(apiUrl: string): string {
  return `${window.location.pathname}-${apiUrl}`
}

export function specificAbort<T extends Function>(serviceFunction: T, customAbortKey: string): T {
  const newServiceFunc = (async (...args: any[]): Promise<any> => {
    const prevHeaders = getPreviousHeaders(OpenAPI)
    OpenAPI.HEADERS = {
      ...prevHeaders,
      [ABORT_KEY]: customAbortKey
    }
    const result = await serviceFunction?.apply(this, args)
    return result
  }) as Function
  return newServiceFunc as T
}

export function skipAbortController<T extends Function>(serviceFunction: T): T {
  const newServiceFunc = (async (...args: any[]): Promise<any> => {
    OpenAPI.HEADERS = {
      [ABORT_SKIP_KEY]: 'skip'
    }
    const result = await serviceFunction?.apply(this, args)
    return result
  }) as Function
  return newServiceFunc as T
}

/**
 * Adds abort middleware and retry logic to API calls
 * @param apiUrl The API URL
 * @param func The original API function
 * @param retryConfig Optional retry configuration
 * @param module The module name for tracking
 * @returns A wrapped function with abort and retry capabilities
 */
export function addAbortMiddleWare(
  apiUrl: string,
  func: Function,
  retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG,
  module: string = 'unknown'
): (...args: any[]) => Promise<any> {
  return async (...args): Promise<any> => {
    const prevHeaders = getPreviousHeaders(OpenAPI)
    const targetAbortkey = prevHeaders?.[ABORT_KEY] ? prevHeaders[ABORT_KEY] : createAbortKey(apiUrl)
    OpenAPI.HEADERS = async () => {
      return {
        ...prevHeaders,
        [ABORT_KEY]: targetAbortkey
      }
    }

    const requestMiddleWare: Interceptors<AxiosRequestConfig>['_fns'][0] = (e) => {
      // Note: requestTime and module are already set by global interceptor (lines 522, 525)
      // This middleware only handles abort/cancellation logic specific to this API call
      handleRequestAbortAndTimestamp(e, targetAbortkey)

      return e
    }

    const responseMiddleware: Interceptors<AxiosResponse>['_fns'][0] = async (e) => {
      // Handle successful response
      const abortKey = get(e, `config.headers[${ABORT_KEY}]`, null) as string | null
      if (CURRENT_CONFIG.enableAbort && abortKey) {
        abortMap.delete(abortKey)
      }

      // Metrics tracking is done in global interceptor
      return e
    }

    const resultFunc = async () => {
      // Create a function that will be executed with retry logic
      const executeApiCall = async () => {
        try {
          const promise = func?.apply(this, args)
          const result = await promise
          return result
        } catch (err) {
          // Handle aborted requests - don't retry these
          if (get(err, 'name', null) === ABORT_ERROR_NAME || get(err, 'code', null) === ABORT_CODE) {
            const obj = abortMap.get(targetAbortkey)
            if (obj && obj.promise) {
              return await obj.promise
            }
          }
          // Propagate the error to be handled by the retry mechanism
          throw err
        }
      }

      // Execute the API call with retry logic
      return executeWithRetry(executeApiCall, retryConfig)
    }

    OpenAPI.interceptors.request.use(requestMiddleWare)
    OpenAPI.interceptors.response.use(responseMiddleware as any)
    const resultPromise = resultFunc()

    const obj = abortMap.get(targetAbortkey) || {}
    abortMap.set(targetAbortkey, { ...obj, promise: resultPromise })

    const result = await resultPromise
    OpenAPI.interceptors.request.eject(requestMiddleWare)
    OpenAPI.interceptors.response.eject(responseMiddleware)
    return result
  }
}

// Helper function to extract status code from error
function getStatusCode(error: any): string | number {
  return error.status || error.response?.status || error.statusCode || 'unknown'
}

// Helper function to extract numeric status code for metrics
function getNumericStatusCode(error: any): number {
  const status = error.status || error.response?.status || error.statusCode
  return typeof status === 'number' ? status : 0
}

// Helper function to extract error message
function getErrorMessage(error: any): string {
  if (typeof error.body === 'string') return error.body
  return error.body?.content || error.body?.message || error.body?.error || error.message || error.data?.detail || 'Something went wrong'
}

// Helper function to extract module from current path
function getModuleFromPath(): string {
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  return pathSegments[0] || 'base'
}

// Helper function to get previous headers
function getPreviousHeaders(OpenAPI: any): any {
  return typeof OpenAPI.HEADERS === 'function' ? {} : OpenAPI.HEADERS
}

// ============================================================================
// Service Proxy Wrapper
// ============================================================================

function getProxy<T extends Record<string, any>>(service: T) {
  const proxy = new Proxy(service, {
    get: (target, prop, receiver) => {
      if (prop === 'prototype') {
        return Reflect.get(target, prop, receiver)
      }
      const origMethod = target[prop as string]
      return async (...args: any[]) => {
        const funcStr = origMethod.toString()
        const url = funcStr.match(/url:\s*['"]([^'"]+)['"]/)?.[1]
        const module = getModuleFromPath()

        args = addTableIdToRequest(prop as string, args)
        // Use the retry mechanism with abort middleware
        const result = await addAbortMiddleWare(url, origMethod, DEFAULT_RETRY_CONFIG, module).apply(this, args)

        updateTableId(prop as string, result)

        return result
        // Note: Error handling (logging, tracking, Slack, Sentry) is done by global interceptor
      }
    }
  })
  return proxy as T
}

// ============================================================================
// Global Interceptor Setup
// ============================================================================

// Set up request interceptor for all OpenAPI instances
function setupGlobalInterceptors() {
  const requestInterceptor = (config: AxiosRequestConfig) => {
    // Delete auth token from headers [Testing]
    // delete config.headers['Authorization']
    // Get user data for sandbox check
    let userObject: Record<string, any> = {}
    try {
      userObject = getSession('data') || {}
    } catch (e) {
      console.error('Error parsing user data from localStorage:', e)
    }

    // Check if request should be blocked for sandbox users
    if (config.url && config.method && shouldBlockForUser(userObject)) {
      if (shouldBlockRequestForSandbox(config.method, config.url)) {
        const error = new Error('This action is prohibited in demo mode.')
        ;(error as any).isBlocked = true
        throw error
      }
    }

    // Add request time for metrics
    config.requestTime = Date.now()

    // Add module from path
    config.module = getModuleFromPath()

    // Add user claims header for backend authorization
    // This allows backend to read user context (org, access_level, is_internal) directly from the header instead of fetching from Firestore on every request
    const claimsHeader = userClaimsService.getClaimsHeader()
    if (claimsHeader) {
      config.headers = config.headers || {}
      config.headers[userClaimsService.getHeaderKey()] = claimsHeader
    }

    return config
  }

  // Combined response/error interceptor (OpenAPI client only accepts one handler)
  const responseInterceptor = async (response: AxiosResponse | any) => {
    // Check if it's an error (either Error instance or error response)
    const isError = response instanceof Error || response.status >= 400 || response.response || response.config?.signal?.aborted

    if (isError) {
      // Special handling for network errors
      if (response.message === 'Network Error' || response.code === 'ERR_NETWORK') {
        const url = response.config?.url || 'unknown'
        const method = response.config?.method || 'unknown'

        // Log network error to Loki
        try {
          lokiLogger.logApiError(
            url,
            method,
            0, // Network errors have no HTTP status code
            `Network Error: ${response.message}`,
            {
              errorCode: response.code,
              errorMessage: response.message,
              isNetworkError: true
            }
          )
        } catch (lokiError) {
          console.warn('Failed to log network error to Loki:', lokiError)
        }
      }

      // Handle the error (including other types of errors)
      handleResponseError(response)
      throw response
    }

    // Track successful response metrics
    if (response.config) {
      trackHttpLatencyMetrics(response.config, response.status, 'success')
    }
    return response
  }

  // Apply to all OpenAPI instances
  TSOpenAPI.interceptors.request.use(requestInterceptor)
  TSOpenAPI.interceptors.response.use(responseInterceptor)

  TSAgentOpenAPI.interceptors.request.use(requestInterceptor)
  TSAgentOpenAPI.interceptors.response.use(responseInterceptor)

  TSTruROIAgentOpenAPI.interceptors.request.use(requestInterceptor)
  TSTruROIAgentOpenAPI.interceptors.response.use(responseInterceptor)

  TSAppClientOpenAPI.interceptors.request.use(requestInterceptor)
  TSAppClientOpenAPI.interceptors.response.use(responseInterceptor)
}

// ============================================================================
// Service Initialization
// ============================================================================

// Service initialization helper
function initializeServices() {
  return {
    // Frontend Client Services
    OpenAPI: TSOpenAPI,
    DefaultService: getProxy(TSDefaultService),
    AdminService: getProxy(TSAdminService),
    BnamesService: getProxy(TSBnamesService),
    UsersService: getProxy(TSUsersService),
    JeFiltersService: getProxy(TSJeFiltersService),
    DigitalStoresService: getProxy(TSDigitalStoresService),
    ModuleService: getProxy(TSModuleService),
    NotificationsService: getProxy(TSNotificationsService),
    OwnersService: getProxy(TSOwnersService),
    ReportsService: getProxy(TSReportsService),
    StoreAvailabilityService: getProxy(TSStoreAvailabilityService),
    MarketingService: getProxy(TSMarketingService),
    LeaderboardService: getProxy(TSLeaderboardService),
    CompassService: getProxy(TSCompassService),
    ChainsService: getProxy(TSChainsService),
    AccountingService: getProxy(TSAccountingService),
    FpnaService: getProxy(TSFpnaService),
    OnboardingService: getProxy(TSOnboardingService),
    JeCustomizationService: getProxy(TSJeCustomizationService),

    // TruROI Agent Client Services
    TruROIAgentOpenAPI: TSTruROIAgentOpenAPI,
    TruROIAgentDefaultService: getProxy(TSTruROIAgentDefaultService),
    TruROIAgentAdminConversationsService: getProxy(TSTruROIAgentAdminConversationsService),

    // Agent Client Services
    AgentOpenAPI: TSAgentOpenAPI,
    AgentDefaultService: getProxy(TSAgentDefaultService),
    AgentBalanceService: getProxy(TSBalanceService),
    AgentSearchService: getProxy(TSSearchService),

    // App Client Services
    AppClientOpenAPI: TSAppClientOpenAPI,
    AppClientFiltersService: getProxy(TSAppClientFiltersService)
  }
}

// Setup global interceptors
setupGlobalInterceptors()

// Initialize all services
const services = initializeServices()

// Destructure for individual exports
const {
  OpenAPI,
  DefaultService,
  AdminService,
  BnamesService,
  JeCustomizationService,
  UsersService,
  DigitalStoresService,
  ModuleService,
  NotificationsService,
  OwnersService,
  ReportsService,
  StoreAvailabilityService,
  MarketingService,
  LeaderboardService,
  CompassService,
  ChainsService,
  AccountingService,
  FpnaService,
  OnboardingService,
  TruROIAgentOpenAPI,
  TruROIAgentDefaultService,
  TruROIAgentAdminConversationsService,
  AgentOpenAPI,
  AgentDefaultService,
  AgentBalanceService,
  AgentSearchService,
  JeFiltersService,
  AppClientOpenAPI,
  AppClientFiltersService
} = services

// ============================================================================
// Re-exports and Named Exports
// ============================================================================

// Re-export everything from client libraries
// @ts-ignore
export * from '@LoopKitchen/agent-client'
// @ts-ignore
export * from '@LoopKitchen/frontend-client'
// @ts-ignore
export * from '@LoopKitchen/truroi-agent-client'
// Re-export specific types from app-client to avoid conflicts (e.g., LoopModule is also in frontend-client)
export type { DateFilterConfigResponse, ModuleData, BNameEndDate } from '@LoopKitchen/app-client'

export {
  AccountingService,
  AdminService,
  AgentBalanceService,
  AgentDefaultService,
  AgentOpenAPI,
  AgentSearchService,
  BnamesService,
  ChainsService,
  CompassService,
  DefaultService,
  DigitalStoresService,
  FpnaService,
  JeCustomizationService,
  JeFiltersService,
  LeaderboardService,
  MarketingService,
  ModuleService,
  NotificationsService,
  OnboardingService,
  OpenAPI,
  OwnersService,
  ReportsService,
  StoreAvailabilityService,
  TruROIAgentDefaultService,
  TruROIAgentOpenAPI,
  TruROIAgentAdminConversationsService,
  UsersService,
  AppClientOpenAPI,
  AppClientFiltersService
}
